import React, { useState, useRef, useEffect } from 'react';
import { SalesStageScoring } from '../services/api';
import DealsListModal from './DealsListModal';
import OpenCyclesListModal from './OpenCyclesListModal';
import Modal from './Modal';
import SelectionModal, { SelectionOption } from './SelectionModal';
import { FilterBar } from './filters';

interface SalesStagesScoringProps {
  stages: SalesStageScoring[];
  isLoading?: boolean;
  isFetching?: boolean; // New prop for subtle loading during filter changes
  selectedCycleTypes?: string[];
  onCycleTypesChange?: (cycleTypes: string[]) => void;
  selectedAccountValues?: string[];
  onAccountValuesChange?: (accountValues: string[]) => void;
  selectedMeetingDispositions?: string[];
  onMeetingDispositionsChange?: (meetingDispositions: string[]) => void;
  selectedSalesRep?: string | null;
  onSalesRepChange?: (salesRep: string | null) => void;
  selectedTeam?: string | null;
  onTeamChange?: (team: string | null) => void;
  hideHeader?: boolean; // New prop to hide the header section
  defaultFiltersCollapsed?: boolean; // New prop to control default filter state
}

interface ModalContent {
  type: 'active-deals' | 'exit-criteria' | 'key-criteria' | 'deal-breakdown' | 'deals-list';
  title: string;
  subtitle?: string;
  stageName: string;
  data: any;
}


const SalesStagesScoring: React.FC<SalesStagesScoringProps> = ({
  stages,
  isLoading,
  isFetching,
  selectedCycleTypes,
  onCycleTypesChange,
  selectedAccountValues,
  onAccountValuesChange,
  selectedMeetingDispositions,
  onMeetingDispositionsChange,
  selectedSalesRep,
  onSalesRepChange,
  selectedTeam,
  onTeamChange,
  hideHeader = false,
  defaultFiltersCollapsed = true
}) => {

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    stageName: string;
    icon: string;
    dealsCount: number;
    modalType: 'attention' | 'category';
    category?: string;
    categoryDisplayName?: string;
    qualificationType?: 'INVESTIGATE' | 'FORGET' | 'ENTERPRISE' | 'TRANSACTIONAL';
    actionType?: 'OPEN_DEAL' | 'INVESTIGATE';
  }>({
    isOpen: false,
    stageName: '',
    icon: '',
    dealsCount: 0,
    modalType: 'attention',
  });

  const [standardModalState, setStandardModalState] = useState<{
    isOpen: boolean;
    content: ModalContent | null;
  }>({
    isOpen: false,
    content: null,
  });

  const [unqualifiedModalState, setUnqualifiedModalState] = useState<{
    isOpen: boolean;
    investigateCount: number;
    forgetCount: number;
  }>({
    isOpen: false,
    investigateCount: 0,
    forgetCount: 0,
  });

  const [qualifiedModalState, setQualifiedModalState] = useState<{
    isOpen: boolean;
    enterpriseOpenDealCount: number;
    enterpriseInvestigateCount: number;
    transactionalOpenDealCount: number;
    transactionalInvestigateCount: number;
  }>({
    isOpen: false,
    enterpriseOpenDealCount: 0,
    enterpriseInvestigateCount: 0,
    transactionalOpenDealCount: 0,
    transactionalInvestigateCount: 0,
  });

  // NEW: Fetch open cycles qualification breakdown data (5 categories - Conflict removed)
  const [qualificationBreakdown, setQualificationBreakdown] = useState<{
    transactional_deal_count: number;
    transactional_zero_count: number;
    enterprise_count: number;
    nurture_count: number;
    bad_fit_count: number;
    trash_count: number;
  } | null>(null);

  // NEW: State for OpenCyclesListModal
  const [openCyclesModalState, setOpenCyclesModalState] = useState<{
    isOpen: boolean;
    category: 'newly_originated' | 'old_with_touchbase' | 'old_without_touchbase' | 'all';
    categoryDisplayName: string;
  }>({
    isOpen: false,
    category: 'all',
    categoryDisplayName: 'All Open Cycles'
  });

  // Loading states
  const [isLoadingBreakdowns, setIsLoadingBreakdowns] = useState(false);

  // Parse meeting dispositions to convert ENTERPRISE_DEAL/ENTERPRISE_ZERO to base dispositions
  // This is needed because the backend expects base values like 'ENTERPRISE', 'TRANSACTIONAL'
  const parseDispositionsForAPI = (dispositions?: string[]): string[] => {
    if (!dispositions || dispositions.length === 0) {
      return [];
    }

    const parsedDispositions: string[] = [];

    for (const d of dispositions) {
      if (d === 'TRANSACTIONAL_DEAL' || d === 'TRANSACTIONAL_ZERO') {
        parsedDispositions.push('TRANSACTIONAL');
      } else if (d === 'ENTERPRISE_DEAL' || d === 'ENTERPRISE_ZERO') {
        parsedDispositions.push('ENTERPRISE');
      } else {
        // Regular dispositions: TRANSACTIONAL, ENTERPRISE, NURTURE, NOT_ANALYZED, BAD_FIT, TRASH
        parsedDispositions.push(d);
      }
    }

    // Remove duplicates
    return Array.from(new Set(parsedDispositions));
  };

  useEffect(() => {
    const fetchBreakdowns = async () => {
      console.log('🔄 Starting to fetch qualification breakdowns...');
      setIsLoadingBreakdowns(true);

      try {
        const { salesCyclesApi } = await import('../services/api');

        // Parse meeting dispositions to base values before sending to API
        const parsedDispositions = parseDispositionsForAPI(selectedMeetingDispositions);

        // Fetch new qualification breakdown with ALL filters
        const qualificationData = await salesCyclesApi.getOpenCyclesQualificationBreakdown(
          selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined,
          selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          selectedSalesRep,
          selectedTeam,
          parsedDispositions.length > 0 ? parsedDispositions : undefined
        );

        console.log('🔍 Qualification breakdown data fetched:', qualificationData);
        setQualificationBreakdown(qualificationData);
      } catch (error) {
        console.error('❌ Error fetching qualification breakdown data:', error);
        // Set default values if API fails
        setQualificationBreakdown({
          transactional_deal_count: 0,
          transactional_zero_count: 0,
          enterprise_count: 0,
          nurture_count: 0,
          bad_fit_count: 0,
          trash_count: 0
        });
      } finally {
        setIsLoadingBreakdowns(false);
        console.log('✅ Qualification breakdown loading complete');
      }
    };

    // Fetch breakdowns immediately when component mounts and when filters change
    fetchBreakdowns();
  }, [selectedCycleTypes, selectedAccountValues, selectedSalesRep, selectedTeam, selectedMeetingDispositions]);

  // Click handlers for different elements
  const handleActiveDealsClick = (stageName: string, activeDeals: number) => {
    const stage = stages.find(s => s.stage_name === stageName);

    // Special handling for open cycles stage - use OpenCyclesListModal
    if (stageName === 'Open sales cycles with no deals') {
      setOpenCyclesModalState({
        isOpen: true,
        category: 'all', // Show all open cycles
        categoryDisplayName: 'All Open Cycles',
      });
      return;
    }
    
    // Use the actual stage name for API calls (backend expects full names)
    // Only map for display purposes in the modal title
    setModalState({
      isOpen: true,
      stageName: stageName, // Pass the full stage name to the API
      icon: stage?.icon || '📋',
      dealsCount: activeDeals,
      modalType: 'category',
      category: 'active',
      categoryDisplayName: 'Active Deals',
    });
  };

  const handleDealBreakdownClick = (stageName: string, type: string, count: number) => {
    const stage = stages.find(s => s.stage_name === stageName);

    // Special handling for open cycles stage with NEW qualification categories
    if (stageName === 'Open sales cycles with no deals') {
      // Handle the qualification categories (Conflict and TRANSACTIONAL_ZERO removed)
      if (['TRANSACTIONAL_DEAL', 'ENTERPRISE', 'NURTURE', 'BAD_FIT', 'TRASH'].includes(type)) {
        const categoryDisplayNames: Record<string, string> = {
          'TRANSACTIONAL_DEAL': 'Open Transactional Deal',
          'ENTERPRISE': 'Open Enterprise Stage Zero',
          'NURTURE': 'Nurture',
          'BAD_FIT': 'Bad Fit',
          'TRASH': 'Trash'
        };

        setModalState({
          isOpen: true,
          stageName: 'Open sales cycles with no deals',
          icon: stage?.icon || '📁',
          dealsCount: count,
          modalType: 'category',
          category: type, // Use the qualification category directly
          categoryDisplayName: categoryDisplayNames[type] || type,
        });
      }
      // OLD: Keep backward compatibility for "Qualified" and "Not qualified" (deprecated)
      else if (type === 'Qualified') {
        console.log('Opening qualified modal - DEPRECATED');
        setQualifiedModalState({
          isOpen: true,
          enterpriseOpenDealCount: 0,
          enterpriseInvestigateCount: 0,
          transactionalOpenDealCount: 0,
          transactionalInvestigateCount: 0,
        });
      } else if (type === 'Not qualified') {
        console.log('Opening unqualified modal - DEPRECATED');
        setUnqualifiedModalState({
          isOpen: true,
          investigateCount: 0,
          forgetCount: 0,
        });
      }
      return;
    }
    
    // Use the actual stage name for API calls (backend expects full names)
    
    // Map to backend categories
    let category = '';
    let categoryDisplayName = type;
    
    // Map "all criteria" categories to 3-criteria (or appropriate backend category)
    if (type.includes('all') || type.includes('criteria met') || type.includes('both criteria')) {
      category = '3-criteria';
    }
    // Map everything else to 1-or-less
    else {
      category = '1-or-less';
    }
    
    if (category) {
      setModalState({
        isOpen: true,
        stageName: stageName, // Pass the full stage name to the API
        icon: stage?.icon || '📋',
        dealsCount: count,
        modalType: 'category',
        category,
        categoryDisplayName,
      });
    }
  };

  // DEPRECATED: Old handlers for backward compatibility with qualified/unqualified system
  // These are no longer used with the new 6-category qualification system
  const handleUnqualifiedOptionSelect = (option: SelectionOption) => {
    console.warn('handleUnqualifiedOptionSelect is deprecated - using new 6-category system');
    // This function is kept for backward compatibility but should not be called
  };

  const handleQualifiedOptionSelect = (option: SelectionOption) => {
    console.warn('handleQualifiedOptionSelect is deprecated - using new 6-category system');
    // This function is kept for backward compatibility but should not be called
  };

  // DEPRECATED: With new 6-category system, back to selection is not needed
  const handleBackToSelection = () => {
    console.warn('handleBackToSelection is deprecated - new 6-category system opens modal directly');
    setModalState({
      isOpen: false,
      stageName: '',
      icon: '',
      dealsCount: 0,
      modalType: 'attention',
    });
  };

  // DEPRECATED: Old modal close handlers for backward compatibility
  const closeUnqualifiedModal = () => {
    setUnqualifiedModalState({
      isOpen: false,
      investigateCount: 0,
      forgetCount: 0,
    });
  };

  const closeQualifiedModal = () => {
    setQualifiedModalState({
      isOpen: false,
      enterpriseOpenDealCount: 0,
      enterpriseInvestigateCount: 0,
      transactionalOpenDealCount: 0,
      transactionalInvestigateCount: 0,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      stageName: '',
      icon: '',
      dealsCount: 0,
      modalType: 'attention',
    });
  };

  const closeStandardModal = () => {
    setStandardModalState({
      isOpen: false,
      content: null,
    });
  };

  const renderModalContent = (content: ModalContent) => {
    switch (content.type) {
      case 'active-deals':
        return (
          <div className="space-y-6">
            <div className="bg-brand-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-brand-900 mb-2">Active Deals Overview</h3>
              <div className="text-3xl font-bold text-accent-blue">{content.data.activeDeals}</div>
              <p className="text-brand-600 text-sm mt-2">
                These are the current deals in the {content.stageName} stage that require attention and management.
              </p>
            </div>
          </div>
        );

      case 'exit-criteria':
        return (
          <div className="space-y-6">
            <div className="bg-brand-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-brand-900 mb-2">Exit Criteria Details</h3>
              <div className="text-lg font-medium text-accent-blue mb-2">{content.data.criteria}</div>
              <p className="text-brand-600 text-sm">
                This exit criteria must be met to advance deals from the {content.stageName} stage.
              </p>
            </div>
          </div>
        );

      case 'deal-breakdown':
        return (
          <div className="space-y-6">
            <div className="bg-brand-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-brand-900 mb-2">Deal Breakdown</h3>
              <div className="text-3xl font-bold text-accent-blue">{content.data.count}</div>
              <div className="text-lg text-brand-700 mb-2">{content.data.type}</div>
              <p className="text-brand-600 text-sm">
                Deals in the {content.stageName} stage matching this criteria.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
        <div className="text-gray-700 dark:text-gray-300 text-sm font-inter font-medium">Loading sales stages...</div>
      </div>
    );
  }

  if (!stages || stages.length === 0) {
    return (
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 shadow-soft">
        <div className="text-center text-white/80 font-inter">
          No sales stages data available
        </div>
      </div>
    );
  }

  const getScoreDisplay = (avgScore: number | null): string => {
    if (avgScore === null) return 'N/A';
    return avgScore.toFixed(1);
  };

  const getScoreBackgroundColor = (avgScore: number | null, stageName: string): string => {
    if (avgScore === null) return 'bg-gray-500 text-white';

    // Get maximum possible score for this stage
    const getMaxScore = (stage: string): number => {
      switch (stage) {
        case 'Discovery': return 1.0;           // Stage 1
        case 'Business Case': return 2.0;       // Stage 1 + Stage 2
        case 'Validation': return 3.0;          // Stage 1 + Stage 2 + Stage 3
        case 'Economic Buyer Meeting': return 4.0; // Stage 1 + Stage 2 + Stage 3 + Stage 4
        case 'Legal & Procurement': return 5.0; // Stage 1 + Stage 2 + Stage 3 + Stage 4 + Stage 5
        default: return 1.0; // fallback
      }
    };

    const maxScore = getMaxScore(stageName);
    const percentage = (avgScore / maxScore) * 100;

    if (percentage < 30) {
      return 'bg-red-500/15 text-red-700 dark:text-red-400 font-bold';
    } else if (percentage >= 30 && percentage <= 70) {
      return 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 font-bold';
    } else {
      return 'bg-green-500/15 text-green-700 dark:text-green-400 font-bold';
    }
  };

  const getStageScoreRange = (stageName: string): string => {
    // Special handling for open cycles stage
    if (stageName === 'Open sales cycles with no deals') {
      return '0';  // No scoring for open cycles
    }
    
    // Cumulative scoring: each stage adds 1 to the total possible score
    console.log('getStageScoreRange called with:', stageName);
    switch (stageName) {
      case 'Discovery': 
        console.log('Returning 1 for Discovery');
        return '1';                    // Stage 1: max score = 1
      case 'Business Case': 
        console.log('Returning 2 for Business Case');
        return '2';                // Stage 2: max score = 2 (1+1)
      case 'Validation': 
        console.log('Returning 3 for Validation');
        return '3';                   // Stage 3: max score = 3 (2+1)
      case 'Economic Buyer Meeting': 
        console.log('Returning 4 for Economic Buyer Meeting');
        return '4';       // Stage 4: max score = 4 (3+1)
      case 'Legal & Procurement': 
        console.log('Returning 5 for Legal & Procurement');
        return '5';          // Stage 5: max score = 5 (4+1)
      default: 
        console.log('Returning default 1 for:', stageName);
        return '1';
    }
  };

  return (
    <section className="space-y-6">
      {!hideHeader && (
        <>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                {/* <BarChart3 className="h-7 w-7 text-gray-700" /> */}
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales Stages and Scoring</h2>
              </div>
              <div className="text-gray-700 dark:text-gray-300 text-sm space-y-1 max-w-md font-inter mt-2">
                <p className="leading-relaxed">Score reflects the completion of exit criteria for each stage</p>
                <p className="leading-relaxed mt-2">
                  <span className="font-semibold">Maximum score:</span>
                </p>
                <p className="text-gray-600 dark:text-gray-400 font-inter">• Per stage: 1</p>
                <p className="text-gray-600 dark:text-gray-400 font-inter">• For deal (total): 5</p>
              </div>
            </div>
            
            {/* Filters */}
            <div className="text-right">
              <FilterBar
                selectedCycleTypes={selectedCycleTypes || []}
                selectedAccountValues={selectedAccountValues || []}
                selectedMeetingDispositions={selectedMeetingDispositions || []}
                selectedTeam={selectedTeam || null}
                selectedSalesRep={selectedSalesRep || null}
                onCycleTypesChange={onCycleTypesChange || (() => {})}
                onAccountValuesChange={onAccountValuesChange || (() => {})}
                onMeetingDispositionsChange={onMeetingDispositionsChange || (() => {})}
                onTeamChange={onTeamChange || (() => {})}
                onSalesRepChange={onSalesRepChange || (() => {})}
                isFetching={isFetching}
                showClearAll={true}
                layout="horizontal"
                className="max-w-4xl"
                collapsible={true}
                defaultCollapsed={defaultFiltersCollapsed}
              />
            </div>
      </div>
        </>
      )}

      {/* Additional info section when header is hidden */}
      {hideHeader && (
        <div className="text-gray-700 dark:text-gray-300 text-sm space-y-1 font-inter">
          <p className="leading-relaxed">
            <span className="font-semibold">Maximum score:</span>
          </p>
          <p className="text-gray-600 dark:text-gray-400 font-inter">• Per stage: 1</p>
          <p className="text-gray-600 dark:text-gray-400 font-inter">• For deal (total): 5</p>
        </div>
      )}

      {/* Filter section when header is hidden - match Trends/Insights style */}
      {hideHeader && (
        <FilterBar
          selectedCycleTypes={selectedCycleTypes || []}
          selectedAccountValues={selectedAccountValues || []}
          selectedMeetingDispositions={selectedMeetingDispositions || []}
          selectedTeam={selectedTeam || null}
          selectedSalesRep={selectedSalesRep || null}
          onCycleTypesChange={onCycleTypesChange || (() => {})}
          onAccountValuesChange={onAccountValuesChange || (() => {})}
          onMeetingDispositionsChange={onMeetingDispositionsChange || (() => {})}
          onTeamChange={onTeamChange || (() => {})}
          onSalesRepChange={onSalesRepChange || (() => {})}
          isFetching={isFetching}
          showClearAll={true}
          collapsible={true}
          defaultCollapsed={defaultFiltersCollapsed}
        />
      )}
      
      {/* Cards Section with overlay loading */}
      <div className="relative">
        {/* Elegant loading indicator - visible but not intrusive */}
        {isFetching && stages.length > 0 && (
          <div className="absolute inset-0 bg-white/30 dark:bg-gray-900/30 backdrop-blur-[1px] z-10 flex items-start justify-center pt-12 rounded-xl">
            <div className="flex items-center space-x-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-md border border-purple-100 dark:border-purple-800">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
              <div className="text-gray-700 dark:text-gray-300 text-sm font-medium font-inter">Updating...</div>
            </div>
          </div>
        )}

        {/* Initial loading state - only when no data exists */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
            <div className="text-gray-700 dark:text-gray-300 text-sm font-inter font-medium">Loading dashboard...</div>
          </div>
        )}

        {/* Always show content (either current data or empty state) */}
        {(!stages || stages.length === 0) && !isLoading && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center shadow-sm">
            <div className="text-gray-900 dark:text-gray-100 text-lg font-inter">No stages data available</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-2 font-inter">Please check your connection and try again.</div>
          </div>
        )}
        
        {stages && stages.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stages.map((stage) => (
          <div key={stage.stage_name} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
            <div className="mb-5">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{stage.icon}</span>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 font-inter">
                    {stage.stage_name}
                  </h3>
                  {stage.stage_name !== 'Open sales cycles with no deals' && (
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 font-inter">
                      Score up to {getStageScoreRange(stage.stage_name)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              {/* Active Deals and Scored Deals - grouped closer together */}
              <div className="space-y-1">
                <div 
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl p-3 -m-1 transition-all group"
                  onClick={() => handleActiveDealsClick(stage.stage_name, stage.active_deals)}
                >
                  <span className="text-gray-600 dark:text-gray-400 text-sm font-medium font-inter">
                    {stage.stage_name === 'Open sales cycles with no deals' ? 'Total Count' : 'Active Deals'}
                  </span>
                  <div className="text-right">
                    <div className="text-gray-900 dark:text-gray-100 font-bold text-2xl group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors font-inter">{stage.active_deals}</div>
                    {stage.stage_name !== 'Open sales cycles with no deals' && stage.total_amount && (
                      <div className="text-gray-500 dark:text-gray-400 text-xs font-inter">${stage.total_amount.toLocaleString()}</div>
                    )}
                  </div>
                </div>
                
                {/* Scored Deals Count and Amount - Only show for regular stages */}
                {stage.stage_name !== 'Open sales cycles with no deals' && (
                  <div className="flex items-center justify-between p-3 -m-1">
                    <span className="text-gray-500 dark:text-gray-400 text-sm font-inter flex items-center">
                      <span className="mr-2">↳</span>
                      Of them, scored deals
                    </span>
                    <div className="text-right">
                      <div className="text-gray-700 dark:text-gray-300 text-sm font-medium font-inter">{stage.scored_deals || 0}</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs font-inter">${stage.scored_deals_amount?.toLocaleString() || '0'}</div>
                    </div>
                  </div>
                )}
                
                {/* Cycles with Transcripts - Only show for Open sales cycles with no deals */}
                {stage.stage_name === 'Open sales cycles with no deals' && (
                  <div className="flex items-center justify-between p-3 -m-1">
                    <span className="text-gray-500 dark:text-gray-400 text-sm font-inter flex items-center">
                      <span className="mr-2">↳</span>
                      Of them, with transcripts (analyzed)
                    </span>
                    <div className="text-right">
                      <div className="text-gray-700 dark:text-gray-300 text-sm font-medium font-inter">{stage.cycles_with_transcripts || 0}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {stage.stage_name !== 'Open sales cycles with no deals' && (
                <div className="flex items-center justify-between p-3 -m-1">
                  <span className="text-gray-600 dark:text-gray-400 text-sm font-inter">
                    Average Score
                  </span>
                  <span className={`px-2 py-1 rounded font-bold text-lg font-inter ${getScoreBackgroundColor(stage.avg_score, stage.stage_name)}`}>
                    {getScoreDisplay(stage.avg_score)}
                  </span>
                </div>
              )}

              {/* Deal Breakdown Sections - Different for open cycles vs regular stages */}
              <div className="space-y-1 mt-4">
                {stage.stage_name === 'Open sales cycles with no deals' ? (
                  <>
                    {/* NEW: 5 Qualification Categories (BAD_FIT and TRASH hidden) */}
                    <div
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
                      onClick={() => handleDealBreakdownClick(stage.stage_name,
                        'TRANSACTIONAL_DEAL',
                        qualificationBreakdown?.transactional_deal_count || 0)}
                    >
                      <span className="text-gray-600 dark:text-gray-400 font-inter">
                        Open Transactional Deal
                      </span>
                      <div className="text-right">
                        <div className="text-cyan-600 font-bold hover:text-cyan-700 transition-colors font-inter">{qualificationBreakdown?.transactional_deal_count || 0}</div>
                      </div>
                    </div>

                    <div
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
                      onClick={() => handleDealBreakdownClick(stage.stage_name,
                        'ENTERPRISE',
                        qualificationBreakdown?.enterprise_count || 0)}
                    >
                      <span className="text-gray-600 dark:text-gray-400 font-inter">
                        Open Enterprise Stage Zero
                      </span>
                      <div className="text-right">
                        <div className="text-purple-600 font-bold hover:text-purple-700 transition-colors font-inter">{qualificationBreakdown?.enterprise_count || 0}</div>
                      </div>
                    </div>

                    <div
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
                      onClick={() => handleDealBreakdownClick(stage.stage_name,
                        'NURTURE',
                        qualificationBreakdown?.nurture_count || 0)}
                    >
                      <span className="text-gray-600 dark:text-gray-400 font-inter">
                        Nurture
                      </span>
                      <div className="text-right">
                        <div className="text-blue-600 font-bold hover:text-blue-700 transition-colors font-inter">{qualificationBreakdown?.nurture_count || 0}</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular stage metrics */}
                    <div 
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
                      onClick={() => handleDealBreakdownClick(stage.stage_name, 
                        'Deals with all exit criteria', 
                        stage.deals_with_exit_criteria_move)}
                    >
                      <span className="text-gray-600 dark:text-gray-400 font-inter">
                        {stage.stage_name === 'Economic Buyer Meeting' 
                          ? 'Deals with exit criteria (move)' 
                          : 'Deals with all exit criteria (move)'}
                      </span>
                      <div className="text-right">
                        <div className="text-green-600 font-bold hover:text-green-700 transition-colors font-inter">{stage.deals_with_exit_criteria_move}</div>
                        <div className="text-green-600/80 text-xs font-inter">${stage.amount_all_criteria?.toLocaleString() || '0'}</div>
                      </div>
                    </div>
                    
                    <div 
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors"
                      onClick={() => handleDealBreakdownClick(stage.stage_name, 
                        stage.stage_name === 'Economic Buyer Meeting' ? 'Deals with no exit criteria' : 'Deals with 1 or less exit criteria', 
                        stage.deals_with_attention)}
                    >
                      <span className="text-gray-600 dark:text-gray-400 font-inter">
                        {stage.stage_name === 'Economic Buyer Meeting' 
                          ? 'Deals with no exit criteria (attention)' 
                          : 'Deals with 1 or less exit criteria (attention)'}
                      </span>
                      <div className="text-right">
                        <div className="text-orange-500 font-bold hover:text-orange-600 transition-colors font-inter">{stage.deals_with_attention}</div>
                        <div className="text-orange-500/80 text-xs font-inter">${stage.amount_one_or_no_criteria?.toLocaleString() || '0'}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Info Section - Only show for Open sales cycles with no deals */}
              {stage.stage_name === 'Open sales cycles with no deals' && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-gray-700 dark:text-gray-300 font-medium text-sm mb-2 font-inter">Qualification categories:</h4>
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                    <ul className="text-gray-600 dark:text-gray-400 text-xs space-y-2 font-inter">
                      <li><span className="font-medium text-cyan-800 dark:text-cyan-400">Open Transactional Deal:</span> Simple process with an immediate buying timeline (≤ 30 days)</li>
                      <li><span className="font-medium text-purple-800 dark:text-purple-400">Open Enterprise Stage Zero:</span> Complex or undefined process with a near-term or undefined buying timeline</li>
                      <li><span className="font-medium text-blue-800 dark:text-blue-400">Nurture:</span> Simple process with a near-term or undefined timeline (&gt;120 days)</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Exit Criteria Identified Block - Only show for regular stages, not open cycles */}
              {stage.stage_name !== 'Open sales cycles with no deals' && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-gray-700 dark:text-gray-300 font-medium text-sm mb-2 font-inter">Exit Criteria:</h4>
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                    <ul className="text-gray-600 dark:text-gray-400 text-xs space-y-1 font-inter">
                      {stage.stage_name === 'Discovery' && (
                        <>
                          <li>• Pain points identified: L1 (personal), L2 (team), L3 (business)</li>
                          <li>• Champion identified</li>
                          <li>• Economic Buyer identified</li>
                        </>
                      )}
                      {stage.stage_name === 'Business Case' && (
                        <>
                          <li>• Business case meeting with our Champion</li>
                          <li>• ROI Calculated by Rep</li>
                          <li>• Rep Understands Paper Process</li>
                        </>
                      )}
                      {stage.stage_name === 'Validation' && (
                        <>
                          <li>• Champion confirmed that Smartcat can solve pain points</li>
                          <li>• Date identified for Economic Buyer business case presentation</li>
                        </>
                      )}
                      {stage.stage_name === 'Economic Buyer Meeting' && (
                        <>
                          <li>• Economic Buyer has accepted Business Case</li>
                        </>
                      )}
                      {stage.stage_name === 'Legal & Procurement' && (
                        <>
                          <li>• Paperwork Under Review</li>
                          <li>• Estimated Contract Signature Date</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}


            </div>
          </div>
        ))}
          </div>
        )}
      </div>

      {/* Deals List Modal - Only for deal stages, not for open cycles qualification categories */}
      {modalState.isOpen && modalState.stageName !== 'Open sales cycles with no deals' && (
        <DealsListModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          stageName={modalState.stageName}
          icon={modalState.icon}
          dealsCount={modalState.dealsCount}
          modalType={modalState.modalType}
          category={modalState.category}
          categoryDisplayName={modalState.categoryDisplayName}
          qualificationType={modalState.qualificationType}
          actionType={modalState.actionType}
          selectedCycleTypes={selectedCycleTypes}
          selectedAccountValues={selectedAccountValues}
          selectedSalesRep={selectedSalesRep}
          selectedTeam={selectedTeam}
        />
      )}

      {/* Open Cycles List Modal - For qualification categories (NURTURE, ENTERPRISE, etc.) */}
      {modalState.isOpen && modalState.stageName === 'Open sales cycles with no deals' && modalState.category && (
        <OpenCyclesListModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          periodLabel="Open sales cycles with no deals"
          category={modalState.category as any}
          categoryDisplayName={modalState.categoryDisplayName || 'All'}
          selectedCycleTypes={selectedCycleTypes}
          selectedAccountValues={selectedAccountValues}
          selectedMeetingDispositions={selectedMeetingDispositions}
          selectedSalesRep={selectedSalesRep}
          selectedTeam={selectedTeam}
        />
      )}

      {/* Open Cycles List Modal - For "All Open Cycles" view */}
      <OpenCyclesListModal
        isOpen={openCyclesModalState.isOpen}
        onClose={() => setOpenCyclesModalState({ ...openCyclesModalState, isOpen: false })}
        periodLabel="Open Sales Cycles"
        category={openCyclesModalState.category}
        categoryDisplayName={openCyclesModalState.categoryDisplayName}
        selectedCycleTypes={selectedCycleTypes}
        selectedAccountValues={selectedAccountValues}
        selectedMeetingDispositions={selectedMeetingDispositions}
        selectedSalesRep={selectedSalesRep}
        selectedTeam={selectedTeam}
      />

      {/* Unified Selection Modal */}
      {/* DEPRECATED: Old unqualified selection modal - kept for backward compatibility */}
      <SelectionModal
        isOpen={unqualifiedModalState.isOpen}
        onClose={closeUnqualifiedModal}
        title="📁 Open cycles with no deals - Not qualified (DEPRECATED)"
        items={[
          {
            label: 'Investigate',
            count: 0,
            option: 'INVESTIGATE'
          },
          {
            label: 'Forget',
            count: 0,
            option: 'FORGET'
          }
        ]}
        onSelectOption={handleUnqualifiedOptionSelect}
        isLoading={isLoadingBreakdowns}
      />

      {/* DEPRECATED: Old qualified selection modal - kept for backward compatibility */}
      <SelectionModal
        isOpen={qualifiedModalState.isOpen}
        onClose={closeQualifiedModal}
        title="📁 Open cycles with no deals - Qualified (DEPRECATED)"
        items={[
          {
            label: 'Enterprise - Open a deal',
            count: 0,
            option: 'ENTERPRISE_OPEN_DEAL' as const
          },
          {
            label: 'Enterprise - Investigate',
            count: 0,
            option: 'ENTERPRISE_INVESTIGATE' as const
          },
          {
            label: 'Transactional - Open a deal',
            count: 0,
            option: 'TRANSACTIONAL_OPEN_DEAL' as const
          },
          {
            label: 'Transactional - Investigate',
            count: 0,
            option: 'TRANSACTIONAL_INVESTIGATE' as const
          }
        ]}
        onSelectOption={handleQualifiedOptionSelect}
        isLoading={isLoadingBreakdowns}
      />

      {/* Standard Modal */}
      <Modal
        isOpen={standardModalState.isOpen}
        onClose={closeStandardModal}
        title={standardModalState.content?.title || ''}
        subtitle={standardModalState.content?.subtitle}
      >
        {standardModalState.content && renderModalContent(standardModalState.content)}
      </Modal>
    </section>
  );
};

export default SalesStagesScoring;